import { dapvApi } from 'services/providers/dapv';
import { ApiEmptyRequest, ApiRequest, ApiResponse } from 'services/providers/dapv.types';
import {
    CreatePersonPayload,
    CreatePersonResponse,
    GetPersonResponse,
    GetPersonPayload,
    GetPersonsResponse,
    UpdatePersonPayload,
    UpdateDisordersPayload,
    CreateProjectPayload,
    UpdateProjectPayload,
    UpdatePersonStatusPayload,
    UpdateResponsiblePersonPayload,
    UpdateOutgoingPayload
} from './person.types';
import { AddDocumentPayload, DeleteDocumentPayload } from 'services/document/document.types';
import { User } from 'types/User';
export type GetPersonsParams = {
    params?: {
        limit?: number;
        offset?: number;
        search?: string;
    };
};

const personApi = dapvApi.injectEndpoints({
    endpoints: (build) => ({
        createPerson: build.mutation<ApiResponse<CreatePersonResponse>, CreatePersonPayload>({
            query: (body: CreatePersonPayload) => ({
                url: '/app/persons',
                method: 'POST',
                body
            }),
            transformResponse: (response: ApiResponse<CreatePersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        deletePerson: build.mutation<ApiResponse<GetPersonResponse>, ApiRequest<any>>({
            query: ({id}: ApiRequest<any>) => ({
                url: `/app/persons/${id}`,
                method: 'DELETE'
            }),
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        getPerson: build.mutation<ApiResponse<GetPersonResponse>, GetPersonPayload>({
            query: ({ id }: GetPersonPayload) => {
                return {
                    url: `/app/persons/${id}`,
                    method: 'GET'
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        getPersons: build.mutation<ApiResponse<GetPersonsResponse>, ApiEmptyRequest>({
            query: () => ({
                url: '/app/persons',
                method: 'GET'
            }),
            transformResponse: (response: ApiResponse<GetPersonsResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        getPersonManagers: build.mutation<ApiResponse<Array<User>>, GetPersonPayload>({
            query: ({id}) => ({
                url: `/app/persons/${id}/managers`,
                method: 'GET'
            }),
            transformResponse: (response: ApiResponse<Array<User>>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        addDocument: build.mutation<ApiResponse<GetPersonResponse>, AddDocumentPayload>({
            query: ({ id, body }: AddDocumentPayload) => {
                return {
                    url: `/app/persons/${id}/documents`,
                    method: 'POST',
                    body
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        updateProfile: build.mutation<ApiResponse<GetPersonResponse>, UpdatePersonPayload>({
            query: ({ id, body }: UpdatePersonPayload) => {
                return {
                    url: `/app/persons/${id}/profile`,
                    method: 'PUT',
                    body
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        updatePerson: build.mutation<ApiResponse<GetPersonResponse>, UpdatePersonPayload>({
            query: ({ id, body }: UpdatePersonPayload) => {
                return {
                    url: `/app/persons/${id}`,
                    method: 'PUT',
                    body
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        updateDisorders: build.mutation<ApiResponse<GetPersonResponse>, UpdateDisordersPayload>({
            query: ({ id, body }: UpdateDisordersPayload) => {
                return {
                    url: `/app/persons/${id}/disorders`,
                    method: 'PUT',
                    body
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        createProject: build.mutation<ApiResponse<GetPersonResponse>, CreateProjectPayload>({
            query: ({ id, label, initial_request }: CreateProjectPayload) => {
                return {
                    url: `/app/persons/${id}/projects`,
                    method: 'POST',
                    body: { label, initial_request }
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        updateProject: build.mutation<ApiResponse<GetPersonResponse>, UpdateProjectPayload>({
            query: ({ id, project_id, label, initial_request }: UpdateProjectPayload) => {
                return {
                    url: `/app/persons/${id}/projects/${project_id}`,
                    method: 'PUT',
                    body: { label, initial_request }
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        updatePersonStatus: build.mutation<ApiResponse<GetPersonResponse>, UpdatePersonStatusPayload>({
            query: ({ id, status }: UpdatePersonStatusPayload) => {
                return {
                    url: `/app/persons/${id}/status`,
                    method: 'PUT',
                    body: { status }
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        updateResponsiblePerson: build.mutation<ApiResponse<GetPersonResponse>, UpdateResponsiblePersonPayload>({
            query: ({ id, body }: UpdateResponsiblePersonPayload) => {
                return {
                    url: `/app/persons/${id}/responsible-person`,
                    method: 'PUT',
                    body
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        updateOutgoing: build.mutation<ApiResponse<GetPersonResponse>, UpdateOutgoingPayload>({
            query: ({ id, body }: UpdateOutgoingPayload) => {
                return {
                    url: `/app/persons/${id}/outgoing`,
                    method: 'PUT',
                    body
                };
            },
            transformResponse: (response: ApiResponse<GetPersonResponse>) => ({
                code: response.code,
                data: response.data,
                status: response.status
            })
        }),
        sendAccessLink: build.mutation<ApiResponse<GetPersonResponse>, ApiRequest<any>>({
            query: ({id}: ApiRequest<any>) => ({
                url: `/app/persons/${id}/send-access-link`,
                method: 'POST'
            })
        })
    })
});

export const {
    useUpdatePersonMutation,
    useGetPersonMutation,
    useCreatePersonMutation,
    useGetPersonsMutation,
    useAddDocumentMutation,
    useUpdateProfileMutation,
    useUpdateDisordersMutation,
    useCreateProjectMutation,
    useUpdateProjectMutation,
    useUpdatePersonStatusMutation,
    useGetPersonManagersMutation,
    useUpdateResponsiblePersonMutation,
    useUpdateOutgoingMutation,
    useDeletePersonMutation,
    useSendAccessLinkMutation
} = personApi;
